import { useEffect, useState } from 'react';
import { Button, Input, SectionCard, Skeleton, StatusBadge, Switch, toast } from '@/components/ui';
import {
  useExchangeRateProviderMutation,
  useExchangeRateProviderQuery,
} from '../hooks/useExchangeRatesQuery';
import { formatDateTime, formatRate } from '../lib/format';
import type { ExchangeRateProviderTestResult } from '../types';

interface ProviderConfigCardProps {
  canWrite: boolean;
}

interface ProviderDraft {
  apiKey: string;
  enabled: boolean;
  scheduleTime: string;
}

const KEY_UNCHANGED = '';

/** "HH:mm" 형식 검증 (빈 값은 허용 — 미설정). <input type="time"> 값은 HH:mm. */
const isValidTime = (v: string): boolean => v === '' || /^([01]\d|2[0-3]):[0-5]\d$/.test(v);

/** 서버 "HH:mm:ss" → 입력용 "HH:mm" (이미 HH:mm 이면 그대로). */
const toInputTime = (v: string | null): string => (v ? v.slice(0, 5) : '');

/** 입력 "HH:mm" → 서버 LocalTime "HH:mm:ss". 빈 값은 null. */
const toLocalTime = (v: string): string | null => (v === '' ? null : `${v}:00`);

const lastStatusVariant = (status: string | null): 'success' | 'danger' | 'neutral' => {
  if (status === 'SUCCESS') return 'success';
  if (status === 'FAILED' || status === 'ERROR') return 'danger';
  return 'neutral';
};

// 마지막 실행 상태 — 내부 enum(SUCCESS/FAILED/ERROR) 대신 사람친화 라벨로 노출.
const LAST_STATUS_LABELS: Record<string, string> = {
  SUCCESS: '성공',
  FAILED: '실패',
  ERROR: '오류',
};

const lastStatusLabel = (status: string | null): string =>
  status ? LAST_STATUS_LABELS[status] ?? status : '실행 이력 없음';

/**
 * 관세청(공공데이터포털) 자동 갱신 공급자 설정 카드.
 * - API 키는 write-only: 마스킹만 표시하고, 비워 두면 기존 키 보존.
 * - "연동 테스트" 는 라이브 호출 후 샘플 환율을 토스트로 보여주고 persist 하지 않는다.
 * - 키 미설정이면 dormant — 자동 배치가 동작하지 않음을 안내한다.
 */
export const ProviderConfigCard = ({ canWrite }: ProviderConfigCardProps) => {
  const query = useExchangeRateProviderQuery();
  const { save, test } = useExchangeRateProviderMutation();

  const [draft, setDraft] = useState<ProviderDraft>({
    apiKey: KEY_UNCHANGED,
    enabled: false,
    scheduleTime: '',
  });
  const [testResult, setTestResult] = useState<ExchangeRateProviderTestResult | null>(null);

  // 서버 값이 도착/갱신되면 draft 동기화(apiKey 는 write-only 라 항상 빈 값으로 유지).
  useEffect(() => {
    if (query.data) {
      setDraft({
        apiKey: KEY_UNCHANGED,
        enabled: query.data.enabled,
        scheduleTime: toInputTime(query.data.scheduleTime),
      });
    }
  }, [query.data]);

  if (query.isPending) {
    return (
      <SectionCard title="자동 갱신 (관세청 연동)">
        <Skeleton className="h-[180px] w-full" />
      </SectionCard>
    );
  }

  if (query.isError) {
    return (
      <SectionCard title="자동 갱신 (관세청 연동)">
        <div
          role="alert"
          className="flex flex-col items-center gap-2 rounded-lg py-8 text-center text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          <p>공급자 설정을 불러오지 못했습니다.</p>
          <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
            다시 시도
          </Button>
        </div>
      </SectionCard>
    );
  }

  const config = query.data;
  const timeValid = isValidTime(draft.scheduleTime);
  const dirty =
    draft.apiKey.trim() !== '' ||
    draft.enabled !== config.enabled ||
    draft.scheduleTime !== toInputTime(config.scheduleTime);

  const handleSave = () => {
    if (!timeValid) {
      toast.error('갱신 시각은 HH:mm 형식이어야 합니다.');
      return;
    }
    save.mutate(
      {
        // 빈 키는 omit → 기존 키 보존. 입력값이 있을 때만 전송.
        ...(draft.apiKey.trim() !== '' ? { apiKey: draft.apiKey.trim() } : {}),
        enabled: draft.enabled,
        scheduleTime: toLocalTime(draft.scheduleTime),
      },
      {
        onSuccess: () => {
          toast.success('자동 갱신 설정이 저장되었습니다.');
          setDraft((prev) => ({ ...prev, apiKey: KEY_UNCHANGED }));
        },
        onError: () => toast.error('자동 갱신 설정 저장에 실패했습니다.'),
      },
    );
  };

  const handleTest = () => {
    setTestResult(null);
    test.mutate(undefined, {
      onSuccess: (result) => {
        setTestResult(result);
        if (result.success) {
          toast.success(result.message ?? '관세청 연동 테스트 성공');
        } else {
          toast.error(result.message ?? '관세청 연동 테스트 실패');
        }
      },
      onError: () => {
        const failed: ExchangeRateProviderTestResult = {
          success: false,
          message: '연동 테스트 호출에 실패했습니다.',
          sampleRates: null,
        };
        setTestResult(failed);
        toast.error(failed.message ?? '연동 테스트 실패');
      },
    });
  };

  return (
    <SectionCard
      title="자동 갱신 (관세청 연동)"
      description="공공데이터포털 관세청 주간환율을 매일 자동 갱신합니다. API 키를 입력하고 연동 테스트로 확인하세요."
    >
      <div className="flex flex-col gap-4">
        {/* 키 상태 / 마지막 실행 요약 */}
        <div
          className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-md px-4 py-3 text-[12px]"
          style={{ background: 'var(--surface-1)', border: '1px solid var(--divider-strong)' }}
        >
          <span className="flex items-center gap-2" style={{ color: 'var(--content-muted)' }}>
            API 키
            {config.apiKeyConfigured ? (
              <StatusBadge label={config.apiKeyMasked ?? '설정됨'} variant="success" size="sm" />
            ) : (
              <StatusBadge label="미설정" variant="neutral" size="sm" />
            )}
          </span>
          <span style={{ color: 'var(--content-muted)' }}>
            마지막 실행 {formatDateTime(config.lastRunAt)}
          </span>
          <span className="flex items-center gap-1.5" style={{ color: 'var(--content-muted)' }}>
            상태
            <StatusBadge
              label={lastStatusLabel(config.lastStatus)}
              variant={lastStatusVariant(config.lastStatus)}
              size="sm"
            />
          </span>
        </div>

        {!config.apiKeyConfigured && draft.apiKey.trim() === '' ? (
          <p
            className="rounded-md px-3 py-2 text-[12px]"
            style={{ background: 'var(--status-warn-bg)', color: 'var(--status-warn-fg)' }}
          >
            API 키가 설정되지 않아 자동 갱신이 비활성(dormant) 상태입니다. 키를 입력하면 활성화됩니다.
          </p>
        ) : null}

        {/* API 키 입력 + 연동 테스트 */}
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="kcs-api-key"
            className="text-[13px] font-medium"
            style={{ color: 'var(--content-strong)' }}
          >
            관세청 API 키
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              id="kcs-api-key"
              type="password"
              autoComplete="off"
              placeholder={config.apiKeyConfigured ? '변경하려면 새 키 입력 (비워두면 유지)' : 'API 키 입력'}
              disabled={!canWrite || save.isPending}
              value={draft.apiKey}
              className="max-w-[420px] flex-1"
              onChange={(e) => setDraft((prev) => ({ ...prev, apiKey: e.target.value }))}
            />
            <Button
              variant="outline"
              size="sm"
              disabled={test.isPending}
              onClick={handleTest}
            >
              {test.isPending ? '테스트 중…' : '연동 테스트'}
            </Button>
          </div>
          <p className="text-[11px]" style={{ color: 'var(--content-muted)' }}>
            연동 테스트는 입력한 키(또는 저장된 키)로 관세청을 라이브 호출하며, 결과는 저장되지 않습니다.
          </p>
        </div>

        {/* 연동 테스트 결과 샘플 환율 */}
        {testResult ? (
          <div
            className="rounded-md px-3 py-2.5 text-[12px]"
            style={{
              background: testResult.success ? 'var(--status-success-bg)' : 'var(--status-danger-bg)',
              color: testResult.success ? 'var(--status-success-fg)' : 'var(--status-danger-fg)',
            }}
            role="status"
            aria-live="polite"
          >
            <p className="font-medium">
              {testResult.success ? '연동 성공' : '연동 실패'}
              {testResult.message ? ` — ${testResult.message}` : ''}
            </p>
            {testResult.success && testResult.sampleRates && testResult.sampleRates.length > 0 ? (
              <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 tabular-nums">
                {testResult.sampleRates.map((s) => (
                  <span key={s.currencyCode}>
                    1 {s.currencyCode} = {formatRate(s.krwPerUnit)} KRW
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {/* 스케줄 + 활성 토글 */}
        <div className="flex flex-wrap items-end gap-6">
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="kcs-schedule"
              className="text-[13px] font-medium"
              style={{ color: 'var(--content-strong)' }}
            >
              자동 갱신 시각
            </label>
            <Input
              id="kcs-schedule"
              type="time"
              disabled={!canWrite || save.isPending}
              value={draft.scheduleTime}
              className="max-w-[160px] tabular-nums"
              aria-invalid={!timeValid}
              onChange={(e) => setDraft((prev) => ({ ...prev, scheduleTime: e.target.value }))}
            />
          </div>

          <label
            className="flex items-center gap-2 pb-2 text-[13px]"
            style={{ color: 'var(--content-default)' }}
          >
            <Switch
              checked={draft.enabled}
              disabled={!canWrite || save.isPending}
              onCheckedChange={(v) => setDraft((prev) => ({ ...prev, enabled: v }))}
              aria-label="자동 갱신 활성"
            />
            {draft.enabled ? (
              <StatusBadge label="자동 갱신 켜짐" variant="success" />
            ) : (
              <StatusBadge label="자동 갱신 꺼짐" variant="neutral" />
            )}
          </label>
        </div>

        {canWrite ? (
          <div className="flex justify-end">
            <Button size="sm" disabled={!dirty || !timeValid || save.isPending} onClick={handleSave}>
              {save.isPending ? '저장 중…' : '설정 저장'}
            </Button>
          </div>
        ) : null}
      </div>
    </SectionCard>
  );
};
